// program-overrides/cand/src/routes/validators/validator.js

// Helper functions
function getQuestionMap(policy) {
        const questions = policy?.questions || [];
        return questions.reduce((map, question) => {
            map[question.question] = question;
            return map;
        }, {});
    }
    
    function getNamedValuesMap(policy) {
        return (policy?.saved_named_values || []).reduce((map, nv) => {
            map[nv.name] = nv.value?.trim() || '';
            return map;
        }, {});
    }
    
    function isAnsweredById(policy, question) {
        if (!question) return false;
        const answers = policy?.answers || {};
        return Boolean(answers[question.id]?.text_answer?.trim());
    }
    
    function getAnswerText(policy, question) {
        if (!question) return '';
        const answers = policy?.answers || {};
        return answers[question.id]?.text_answer?.trim() || '';
    }
    
    // ============= APPLICANT =============
    export function computeApplicantValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const answers = policy.answers || {};
        const namedValues = getNamedValuesMap(policy);
    
        const desiredEffectiveDateQuestion = questionMap['Desired Effective Date'];
        const desiredEffectiveDateAnswered = Boolean(
            desiredEffectiveDateQuestion &&
            answers[desiredEffectiveDateQuestion.id]?.text_answer?.trim()
        );

        const isCandMember = namedValues.cand_member?.toLowerCase().includes('yes');
        const memberSinceQuestion = questionMap['Member since:'];
        const memberSinceAnswered = Boolean(
            memberSinceQuestion &&
            answers[memberSinceQuestion.id]?.text_answer?.trim()
        );

        const allNamedValuesAnswered = [
            namedValues.named_insured,
            namedValues.address_mailing,
            namedValues.contact_name_title,
            namedValues.phone_number,
            namedValues.email,
            namedValues.year_established,
            namedValues.cand_member,
            isCandMember ? memberSinceAnswered : true
        ].every(Boolean);
    
        return desiredEffectiveDateAnswered && allNamedValuesAnswered;
    }
    
    // ============= OPERATIONS =============
    export function computeOperationsValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const namedValues = getNamedValuesMap(policy);
    
        // Base questions
        const businessTypeAnswered = Boolean(namedValues.business_type);
        const operationsAnswered = Boolean(namedValues.operation_list);
        // Revenue questions
        const revenueAnswered = Boolean(namedValues.anticipated_fiscal_year);
        
        // Employee questions
        const fullTimeAnswered = Boolean(namedValues.full_time_employees);
        const partTimeAnswered = Boolean(namedValues.part_time_employees);
        const contractAnswered = Boolean(namedValues.contract_employees);
        const volunteersAnswered = Boolean(namedValues.volunteer_employees);

        // Revenue breakdown
        const canadaRevenueAnswered = Boolean(namedValues.canadian_rev);
    
        if (!businessTypeAnswered || !operationsAnswered ||
            !revenueAnswered ||
            !fullTimeAnswered || !partTimeAnswered || !contractAnswered || !volunteersAnswered ||
            !canadaRevenueAnswered) {
            return false;
        }
    
        // Check if Acupuncture questions are needed
        const services = namedValues.service_list || '';
        const showAcupuncturistQuestion = services.toLowerCase().includes('acupuncture');
        if (showAcupuncturistQuestion) {
            const acupuncturistQuestion = questionMap['Select one of the following'];
            const acupuncturistAnswered = isAnsweredById(policy, acupuncturistQuestion);
            
            if (!acupuncturistAnswered) return false;
    
            const isRegistered = getAnswerText(policy, acupuncturistQuestion) === 'I am a registered acupuncturist';
            if (isRegistered) {
                // Mirror the Operations page: the detail questions below are only
                // shown when the applicant does NOT carry a separate policy and
                // wants their acupuncture operations included on this policy.
                const separatePolicyQuestion = questionMap['Do you carry a separate policy for your Registered Acupuncture operations?'];
                const includeQuestion = questionMap['Would you like to include your Registered Acupuncture operations on this policy?'];

                if (separatePolicyQuestion && !isAnsweredById(policy, separatePolicyQuestion)) return false;
                const noSeparatePolicy = getAnswerText(policy, separatePolicyQuestion).toLowerCase() === 'no';

                if (noSeparatePolicy && includeQuestion && !isAnsweredById(policy, includeQuestion)) return false;
                const includeOnPolicy = noSeparatePolicy && getAnswerText(policy, includeQuestion).toLowerCase() === 'yes';

                if (!includeOnPolicy) return true;

                const organizationAnswered = Boolean(namedValues.acupuncture_organization);
                const memberStandingAnswered = Boolean(namedValues.organization_standing);
                const singleUseNeedlesAnswered = Boolean(namedValues.single_use);
                const otherModalitiesAnswered = Boolean(namedValues.other_modalities);
                
                if (!organizationAnswered || !memberStandingAnswered || 
                    !singleUseNeedlesAnswered || !otherModalitiesAnswered) {;
                    return false;
                }
    
                // If other modalities is yes, need description
                if (namedValues.other_modalities?.toLowerCase() === 'yes') {
                    const otherModalitiesDescQuestion = questionMap['Please describe'];
                    if (!isAnsweredById(policy, otherModalitiesDescQuestion)) return false;
                }
    
                // Previous coverage
                const insurerAnswered = Boolean(namedValues.acupuncture_insurer);
                const expiryAnswered = Boolean(namedValues.acupuncture_expiration);
                const retroAnswered = Boolean(namedValues.acupuncture_retroactive_date);
                if (!insurerAnswered || !expiryAnswered || !retroAnswered) return false;
            }
        }
    
        return true;
    }
    
    // ============= MEDICAL MALPRACTICE =============
    export function computeMedicalMalpracticeValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const namedValues = getNamedValuesMap(policy);
    
        const medmalQuestion = questionMap['Do you require Medical Malpractice Insurance?'];
        if (!isAnsweredById(policy, medmalQuestion)) return false;
    
        const needsMedmal = getAnswerText(policy, medmalQuestion)?.toLowerCase() === 'yes';
        if (!needsMedmal) return true; // Valid if they don't need it
    
        // Coverage type
        const coverageQuestion = questionMap['What coverage are you seeking?'];
        if (!isAnsweredById(policy, coverageQuestion)) return false;
    
        const coverage = getAnswerText(policy, coverageQuestion) || '';
        const needsClinicOnly = coverage.includes('Clinic');
        const needsClinic = coverage.includes('Clinic') || coverage.includes('Both');
        const needsIndividual = coverage.includes('Individual') || coverage.includes('Both');
    
        // Clinic validations
        if (needsClinic) {
            const clinicLimitQuestion = questionMap['Clinic Medical Malpractice Limit Requested'];
            if (!isAnsweredById(policy, clinicLimitQuestion)) return false;

            const individualPolicyQuestion = questionMap['Do you currently have an Individual Medical Malpractice policy with IPMGA?'];
            if (!isAnsweredById(policy, individualPolicyQuestion) && needsClinicOnly) return false;

            const hasIndividualPolicy = getAnswerText(policy, individualPolicyQuestion)?.toLowerCase() === 'yes';
            if (hasIndividualPolicy) {
                const individualLimitQuestion = questionMap['What is your current Individual Malpractice Limit'];
                if (!isAnsweredById(policy, individualLimitQuestion) && needsClinicOnly) return false;
            }

            const modalitiesQuestion = questionMap['Please select all modalities offered at the clinic:'];
            if (modalitiesQuestion && !isAnsweredById(policy, modalitiesQuestion)) return false;

            // Check if "Other" was selected
            const modalities = getAnswerText(policy, modalitiesQuestion) || '';
            if (modalities.includes('Other')) {
                const otherDescQuestion = questionMap['Specify other'];
                if (!isAnsweredById(policy, otherDescQuestion)) return false;
            }
            // Clinic abuse limit
            if (namedValues.clinic_abuse_limit !== undefined && !namedValues.clinic_abuse_limit) return false;
        }
    
        // Individual validations
        if (needsIndividual) {
            const individualLimitQuestion = questionMap['Individual Medical Malpractice Limit Requested'];
            if (!isAnsweredById(policy, individualLimitQuestion)) return false;

            const individualAbuseLimitQuestion = questionMap['Abuse Limit'];
            if (!isAnsweredById(policy, individualAbuseLimitQuestion)) return false;
        }

        // Previous Medical Malpractice Coverage (required for both clinic and individual)
        const insurerAnswered = Boolean(namedValues.med_insurer);
        const expiryAnswered = Boolean(namedValues.med_expiry_date);
        const retroAnswered = Boolean(namedValues.med_retroactive_date);

        if (!insurerAnswered || !expiryAnswered || !retroAnswered) return false;

        // Establishment questions
        const establishmentQuestion = questionMap['Does the Establishment ensure that all of its healthcare staff maintain their own malpractice liability?'];
        if (!isAnsweredById(policy, establishmentQuestion)) return false;

        const annualBasisQuestion = questionMap['As part of the practitioner credentialing process, is evidence of this coverage required on an annual basis?'];
        if (!isAnsweredById(policy, annualBasisQuestion)) return false;

        const informedConsentQuestion = questionMap['Is informed consent obtained prior to all medical procedures/treatments/tests etc.?'];
        if (!isAnsweredById(policy, informedConsentQuestion)) return false;

        const infectionPreventionQuestion = questionMap['Do you follow the current guidance for infection prevention & control issued by the Public Health Agency of Canada or Provincial Health Agency (i.e. – IPAC, SHARPS, safe collection, storage and disposal of hazardous waste, etc.)?'];
        if (!isAnsweredById(policy, infectionPreventionQuestion)) return false;
    
        return true;
    }
    
    // ============= OFFICE PACKAGE =============
export function computeOfficePackageValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const namedValues = getNamedValuesMap(policy);
    
        // First question - must be answered
        const mainQuestion = questionMap['Do you require commercial General Liability and Property Insurance?'];
        if (!isAnsweredById(policy, mainQuestion)) return false;
    
        // Check if they don't need it
        const officeCovers = namedValues.office_coverage || '';
        if (officeCovers.toLowerCase().includes('neither')) return true;
    
        const cglOnly = officeCovers.toLowerCase().includes('cgl only (individual practitioner)');
        const cglAndProperty = officeCovers.toLowerCase().includes('cgl & property');
    
        // ===== CGL ONLY Validation =====
        if (cglOnly) {
            // CGL Limit
            const cglLimitQuestion = questionMap['Commercial General Liability Limit (CGL)'];
            if (!isAnsweredById(policy, cglLimitQuestion)) return false;
    
            // Location address
            const locationAddressQuestion = questionMap['Location Address (CGL)'];
            if (!isAnsweredById(policy, locationAddressQuestion)) return false;
    
            // Multiple locations
            const multiLocationQuestion = questionMap['Do you practice at multiple locations?'];
            if (!isAnsweredById(policy, multiLocationQuestion)) return false;
    
            // If multiple locations, need the list
            const hasMultiLocation = namedValues.cgl_multi_location?.toLowerCase().includes('yes');
            if (hasMultiLocation) {
                const allLocationsQuestion = questionMap['Please list all locations'];
                if (!isAnsweredById(policy, allLocationsQuestion)) return false;
            }
    
            // Additional insureds (only needed if multiple locations)
            if (hasMultiLocation) {
                const additionalInsuredsQuestion = questionMap['Do you require any additional insureds to be listed on your policy?'];
                if (!isAnsweredById(policy, additionalInsuredsQuestion)) return false;
    
                // If yes to AIs, need name and address
                const requireAI = namedValues.require_ai?.toLowerCase().includes('yes');
                if (requireAI) {
                    const aiNameQuestion = questionMap['Additional Insured Name(s)'];
                    const aiAddressQuestion = questionMap['Additional Insured Address(es)'];
                    if (!isAnsweredById(policy, aiNameQuestion)) return false;
                    if (!isAnsweredById(policy, aiAddressQuestion)) return false;
                }
            }
    
            return true;
        }
    
        // ===== CGL & PROPERTY Validation =====
        if (cglAndProperty) {
            // CGL & Property limit
            const limitQuestion = questionMap['Commercial General Liability Limit (CGL & Property)'];
            if (!isAnsweredById(policy, limitQuestion)) return false;
    
            // Location
            const locationQuestion = questionMap['Location Address (CGL & Property)'];
            if (!isAnsweredById(policy, locationQuestion)) return false;
    
            // Multiple locations
            const multiLocationQuestion = questionMap['Do you have multiple locations? (CGL & Property)'];
            if (!isAnsweredById(policy, multiLocationQuestion)) return false;
    
            // If multiple, need list
            const hasMultiLocation = namedValues.multi_location_cgl_property?.toLowerCase().includes('yes');
            if (hasMultiLocation) {
                const allLocationsQuestion = questionMap['Please list all locations (CGL & Property)'];
                if (!isAnsweredById(policy, allLocationsQuestion)) return false;
            }
    
            // Additional insureds (only if multiple locations)
            if (hasMultiLocation) {
                const aiQuestion = questionMap['Do you require any additional insureds to be listed on your policy? (CGL & Property)'];
                if (!isAnsweredById(policy, aiQuestion)) return false;
    
                const requireAI = namedValues.require_cgl_property_ai?.toLowerCase().includes('yes');
                if (requireAI) {
                    const aiNameQuestion = questionMap['Additional Insured Name(s) (CGL & Property)'];
                    const aiAddressQuestion = questionMap['Additional Insured Address(es) (CGL & Property)'];
                    if (!isAnsweredById(policy, aiNameQuestion)) return false;
                    if (!isAnsweredById(policy, aiAddressQuestion)) return false;
                }
            }
    
            // Building details
            const yearBuiltQuestion = questionMap['Year Built'];
            if (!isAnsweredById(policy, yearBuiltQuestion)) return false;
    
            const squareFootageQuestion = questionMap['Square Footage'];
            if (!isAnsweredById(policy, squareFootageQuestion)) return false;
    
            const constructionQuestion = questionMap['Construction'];
            if (!isAnsweredById(policy, constructionQuestion)) return false;
    
            const storeysQuestion = questionMap['Number of Storeys'];
            if (!isAnsweredById(policy, storeysQuestion)) return false;
    
            const leaseOrOwnQuestion = questionMap['Do you lease or Own your Premises?'];
            if (!isAnsweredById(policy, leaseOrOwnQuestion)) return false;
    
            // If owned, check 40 years question
            const leaseOrOwn = namedValues.lease_or_own || '';
            const owned = leaseOrOwn.toLowerCase().includes('own building') || leaseOrOwn.toLowerCase().includes('own condo');
            
            if (owned) {

                const buildingInsuranceQuestion = questionMap['Do you wish to insure building'];
                if (!isAnsweredById(policy, buildingInsuranceQuestion)) return false;

                if (namedValues.building_insurance?.toLowerCase().includes('yes')) {
                    const buildingLimitQuestion = questionMap['Building Limit'];
                    if (!isAnsweredById(policy, buildingLimitQuestion)) return false;
                }

                const over40Question = questionMap['Is the building more than 40 years old?'];
                if (!isAnsweredById(policy, over40Question)) return false;
    
                // If over 40, need update years
                const over40 = namedValues.over_40?.toLowerCase().includes('yes');
                if (over40) {
                    const roofQuestion = questionMap['Roof'];
                    const plumbingQuestion = questionMap['Plumbing'];
                    const heatingQuestion = questionMap['Heating'];
                    const electricalQuestion = questionMap['Electrical'];
    
                    if (!isAnsweredById(policy, roofQuestion)) return false;
                    if (!isAnsweredById(policy, plumbingQuestion)) return false;
                    if (!isAnsweredById(policy, heatingQuestion)) return false;
                    if (!isAnsweredById(policy, electricalQuestion)) return false;
                }
            }
    
            // Protection/distance questions
            const hydrantsQuestion = questionMap['Distance to Hydrants is less than 300m?'];
            if (!isAnsweredById(policy, hydrantsQuestion)) return false;
    
            const firehallQuestion = questionMap['Distance to Firehall is less than 8km?'];
            if (!isAnsweredById(policy, firehallQuestion)) return false;
    
            const sprinkledQuestion = questionMap['100% Sprinkled?'];
            if (!isAnsweredById(policy, sprinkledQuestion)) return false;
    
            const burglarAlarmQuestion = questionMap['Burglar Alarm?'];
            if (!isAnsweredById(policy, burglarAlarmQuestion)) return false;
    
            // Property limits
            const propertyLimitQuestion = questionMap['Please select Property Limit (includes office contents, stock & tenants leasehold improvements)'];
            if (!isAnsweredById(policy, propertyLimitQuestion)) return false;
    
            const contentsQuestion = questionMap['Contents (equipment, fixtures, inventory)'];
            if (!isAnsweredById(policy, contentsQuestion)) return false;
    
            const stockQuestion = questionMap['Stock'];
            if (!isAnsweredById(policy, stockQuestion)) return false;
    
            const tenantsQuestion = questionMap['Tenant/Leasehold Improvements'];
            if (!isAnsweredById(policy, tenantsQuestion)) return false;
    
            // Equipment off premises
            const equipmentOffPremisesQuestion = questionMap['Is equipment taken off premises'];
            if (!isAnsweredById(policy, equipmentOffPremisesQuestion)) return false;
    
            const takenOffPremises = namedValues.taken_off_premises?.toLowerCase().includes('yes');
            if (takenOffPremises) {
                const mobileEquipmentQuestion = questionMap['Please provide total value of mobile equipment'];
                if (!isAnsweredById(policy, mobileEquipmentQuestion)) return false;
            }
    
            // Crime limits
            const employeeDishonestyQuestion = questionMap['Crime Limit - Employee Dishonesty'];
            if (!isAnsweredById(policy, employeeDishonestyQuestion)) return false;
    
            const moneySecuritiesQuestion = questionMap['Crime Limit - Money & Securities'];
            if (!isAnsweredById(policy, moneySecuritiesQuestion)) return false;
    
            // Mortgagee/Loss Payee
            const mortgageeQuestion = questionMap['Do you require a mortgagee/loss payee added to your policy?'];
            if (!isAnsweredById(policy, mortgageeQuestion)) return false;
    
            const requireMortgagee = namedValues.require_mortgagee?.toLowerCase().includes('yes');
            if (requireMortgagee) {
                const legalNameQuestion = questionMap['Legal name'];
                const mailingAddressQuestion = questionMap['Mailing Address'];
                if (!isAnsweredById(policy, legalNameQuestion)) return false;
                if (!isAnsweredById(policy, mailingAddressQuestion)) return false;
            }
    
            return true;
        }
    
        return true;
    }
    
    // ============= DIRECTORS AND OFFICERS LIABILITY =============
export function computeDirectorsAndOfficersValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
    
        // Main question - note it says "Insurance" not "Liability Coverage"
        const mainQuestion = questionMap['Do you require Directors and Officers Insurance?'];
        if (!isAnsweredById(policy, mainQuestion)) return false;
    
        const needsDO = getAnswerText(policy, mainQuestion)?.toLowerCase().includes('no');
        if (needsDO) return true; // Valid if they don't need it (answered "No")
    
        // All D&O questions must be answered (note the & not "and")
        const limitQuestion = questionMap['Directors & Officers Liability Limit Requested'];
        if (!isAnsweredById(policy, limitQuestion)) return false;
    
        const craQuestion = questionMap['Are you in arrears with CRA?'];
        if (!isAnsweredById(policy, craQuestion)) return false;
    
        const breachQuestion = questionMap['Any breach of contracts/debts in the last 3 years?'];
        if (!isAnsweredById(policy, breachQuestion)) return false;
    
        const solventQuestion = questionMap['Is the organization solvent?'];
        if (!isAnsweredById(policy, solventQuestion)) return false;
    
        const turnoverQuestion = questionMap['Have you experienced high turnover in the past 2 years?'];
        if (!isAnsweredById(policy, turnoverQuestion)) return false;
    
        const hiringQuestion = questionMap['Do you have written hiring/interview policies?'];
        if (!isAnsweredById(policy, hiringQuestion)) return false;
    
        const hrQuestion = questionMap['Do you have a human resources department?'];
        if (!isAnsweredById(policy, hrQuestion)) return false;
    
        const disciplineQuestion = questionMap['Do you have written discipline/termination policies?'];
        if (!isAnsweredById(policy, disciplineQuestion)) return false;
    
        // Prior D&O coverage - these are answered by question ID, not named values
        const insurerQuestion = questionMap['Insurer (D&O)'];
        if (!isAnsweredById(policy, insurerQuestion)) return false;
    
        const expiryQuestion = questionMap['Expiry Date (D&O)'];
        if (!isAnsweredById(policy, expiryQuestion)) return false;
    
        const retroQuestion = questionMap['Retroactive Date (if applicable, if date is unknown, please supply date licensed as an ND) (D&O)'];
        if (!isAnsweredById(policy, retroQuestion)) return false;
    
        return true;
    }
    
    // ============= LEGAL EXPENSE =============
    export function computeLegalExpenseValid(policy) {
        if (!policy) return false;
        const namedValues = getNamedValuesMap(policy);

        const legalQuestion = Boolean(namedValues.legal_required);
        
        return legalQuestion;
    }
    
    // ============= CYBER =============
    // ============= CYBER =============
export function computeCyberValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const namedValues = getNamedValuesMap(policy);
    
        // Main question - note exact text
        const cyberQuestion = questionMap['Do you Require Cyber Insurance?'];
        if (!isAnsweredById(policy, cyberQuestion)) return false;
    
        // Check named value to see if they need cyber
        const needsCyber = namedValues.cyber_required === 'yes';
        if (!needsCyber) return true; // Valid if they don't need it
    
        // All cyber questions must be answered with exact question text
        const eventQuestion = questionMap['Have you experienced a cyber event in the past three years that has resulted in a direct financial loss of more than CAD10,000?'];
        if (!isAnsweredById(policy, eventQuestion)) return false;
    
        const legalActionQuestion = questionMap['Have you had any legal action brought or threatened against you in the last five years as a direct result of a cyber event?'];
        if (!isAnsweredById(policy, legalActionQuestion)) return false;
    
        const regulatoryQuestion = questionMap['Have you had any regulatory action initiated against you in the last five years as a direct result of a cyber event?'];
        if (!isAnsweredById(policy, regulatoryQuestion)) return false;
    
        const cannabisQuestion = questionMap['Have you been involved in the direct supply of goods or services to the cannabis industry, nor are you involved directly with the use or supply of cryptocurrency?'];
        if (!isAnsweredById(policy, cannabisQuestion)) return false;
    
        const privacyQuestion = questionMap['Cyber Privacy Liability'];
        if (!isAnsweredById(policy, privacyQuestion)) return false;
    
        const crimeQuestion = questionMap['Cyber Crime'];
        if (!isAnsweredById(policy, crimeQuestion)) return false;
    
        return true;
    }
    
    // ============= PRIOR INSURANCE AND CLAIMS =============
export function computePriorInsuranceAndClaimsValid(policy) {
        if (!policy) return false;
    
        const questionMap = getQuestionMap(policy);
        const namedValues = getNamedValuesMap(policy);
    
        // First question - exact text
        const priorQuestion = questionMap['Have any of your policies ever been declined, cancelled or non-renewed?'];
        if (!isAnsweredById(policy, priorQuestion)) return false;
    
        // Check if they answered yes
        const hadPrior = getAnswerText(policy, priorQuestion)?.toLowerCase().includes('yes');
        if (hadPrior) {
            const detailsQuestion = questionMap['Provide details'];
            if (!isAnsweredById(policy, detailsQuestion)) return false;
        }
    
        // Claims question - exact text
        const claimsQuestion = questionMap['Have any of your policies had any claims in the last 5 years?'];
        if (!isAnsweredById(policy, claimsQuestion)) return false;
    
        // Check named value for claims
        const hasClaims = namedValues.has_claims?.toLowerCase().includes('yes');
        if (hasClaims) {
            const claimDescQuestion = questionMap['Please provide the description of claim, including the date of loss and payout amount'];
            if (!isAnsweredById(policy, claimDescQuestion)) return false;
    
            // Validate Collection data
            const collectionAnswer = policy.answers?.[claimDescQuestion?.id]?.text_answer;
            if (collectionAnswer) {
                try {
                    const parsed = JSON.parse(collectionAnswer);
                    if (!parsed.collections || parsed.collections < 1) return false;
                    
                    const hasAnyData = Object.values(parsed.answers || {}).some(arr => 
                        arr && arr[0] && arr[0].text_answer && arr[0].text_answer.trim() !== ''
                    );
                    if (!hasAnyData) return false;
                } catch (e) {
                    return false;
                }
            }
        }

        // Request higher or different limits question
        const requestQuestion = questionMap['I would like to request higher or different limits for one or more of the coverages selected in this application'];
        if (!isAnsweredById(policy, requestQuestion)) return false;
    
        return true;
    }
    
    // ============= SUMMARY =============
    export function computeSummaryValid(policy) {
        // Summary is typically always valid once you reach it
        // Add any summary-specific validation here if needed
        return true;
    }